import * as RS from '@radix-ui/react-switch';
import { forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { cn } from './cn';

/*
  Switch — тумблер: действие применяется сразу (R: тумблер = сразу, чекбокс = часть формы).
  Включённое состояние — brand-solid (coral-600: ≥ 3:1 к холсту как UI-элемент).
*/

interface SwitchProps extends ComponentPropsWithoutRef<typeof RS.Root> {
  label?: ReactNode;
  description?: ReactNode;
  size?: 'sm' | 'md';
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  ({ label, description, size = 'md', className, id, ...rest }, ref) => {
    const reactId = useId();
    const switchId = id ?? `switch-${reactId}`;
    const control = (
      <RS.Root
        ref={ref}
        id={switchId}
        data-slot="switch"
        className={cn(
          'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
          'transition-colors duration-normal ease-snappy',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'data-[state=checked]:bg-brand-solid data-[state=unchecked]:bg-line-strong',
          size === 'md' ? 'h-5 w-9' : 'h-4 w-7',
          className,
        )}
        {...rest}
      >
        <RS.Thumb
          className={cn(
            'pointer-events-none block rounded-full bg-card shadow-sm transition-transform duration-normal ease-snappy',
            size === 'md'
              ? 'size-4 data-[state=checked]:translate-x-4'
              : 'size-3 data-[state=checked]:translate-x-3',
          )}
        />
      </RS.Root>
    );
    if (!label) return control;
    return (
      <div className="flex items-start justify-between gap-4">
        <label htmlFor={switchId} className="flex cursor-pointer flex-col gap-0.5">
          <span className="text-sm font-medium text-fg">{label}</span>
          {description && <span className="text-xs text-fg-muted">{description}</span>}
        </label>
        {control}
      </div>
    );
  },
);
Switch.displayName = 'Switch';
